import type { DataSourceId } from '../../contexts/DataSourceContext';
import { isWelcomeSourceId } from './source-catalog';
import {
  finalizeWelcomeResumeIntent,
  readWelcomeResumeIntent,
  resolveWelcomeResumeIntent,
} from './resume-intent';

type WelcomeOAuthReturnStatus = 'success' | 'denied' | 'failed';

export interface WelcomeOAuthReturnResult {
  handled: boolean;
  sourceId?: DataSourceId;
  status?: WelcomeOAuthReturnStatus;
  reasonCode?: string;
  nextRoute?: string;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function sourceIdFromOAuthState(state: string | null | undefined): DataSourceId | null {
  if (!state) return null;
  const candidate = state.split(':')[0];
  return isWelcomeSourceId(candidate) ? candidate : null;
}

function stateMatchesIntent(state: string | null | undefined, intent: { sourceId: DataSourceId; correlationId: string }): boolean {
  if (!state) return true;
  return state === intent.correlationId
    || state === intent.sourceId
    || state.startsWith(`${intent.sourceId}:`);
}

function welcomeReturnRoute(result: {
  sourceId: DataSourceId;
  status: WelcomeOAuthReturnStatus;
  reasonCode?: string;
}): string {
  const params = new URLSearchParams({
    welcome: 'oauth-return',
    source: result.sourceId,
    status: result.status,
  });
  if (result.reasonCode) params.set('reason', result.reasonCode);
  return `/welcome?${params.toString()}`;
}

export function resolveWelcomeOAuthReturn(input: {
  storage?: StorageLike;
  state?: string | null;
  sourceId?: DataSourceId | null;
  status: WelcomeOAuthReturnStatus;
  reasonCode?: string;
  now?: number;
}): WelcomeOAuthReturnResult {
  const storage = input.storage ?? sessionStorage;
  const intent = readWelcomeResumeIntent(storage);
  if (!intent) return { handled: false };

  const sourceId = input.sourceId ?? sourceIdFromOAuthState(input.state) ?? intent.sourceId;
  const resolution = resolveWelcomeResumeIntent({
    storage,
    sourceId,
    now: input.now,
  });

  if (!resolution.ok) {
    finalizeWelcomeResumeIntent({
      storage,
      status: 'failed',
      reasonCode: resolution.reasonCode,
      now: input.now,
    });
    return {
      handled: true,
      sourceId,
      status: 'failed',
      reasonCode: resolution.reasonCode,
      nextRoute: welcomeReturnRoute({ sourceId, status: 'failed', reasonCode: resolution.reasonCode }),
    };
  }

  if (!stateMatchesIntent(input.state, resolution.intent)) {
    finalizeWelcomeResumeIntent({
      storage,
      status: 'failed',
      reasonCode: 'state_mismatch',
      now: input.now,
    });
    return {
      handled: true,
      sourceId,
      status: 'failed',
      reasonCode: 'state_mismatch',
      nextRoute: welcomeReturnRoute({ sourceId, status: 'failed', reasonCode: 'state_mismatch' }),
    };
  }

  const finalStatus = input.status === 'success' ? 'returned' : 'failed';
  const reasonCode = input.reasonCode ?? (input.status === 'success' ? 'oauth-returned' : `oauth-${input.status}`);
  finalizeWelcomeResumeIntent({
    storage,
    status: finalStatus,
    reasonCode,
    now: input.now,
  });

  return {
    handled: true,
    sourceId,
    status: input.status,
    reasonCode,
    nextRoute: welcomeReturnRoute({ sourceId, status: input.status, reasonCode }),
  };
}
